import {
    RiBarChart2Line, RiEyeLine, RiLineChartLine, RiPieChartLine, RiTableLine, RiCalendarTodoLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import {
    MetricsDashboardData, MetricsDashboardGraph, MetricsDashboardGraphDataSource, MetricsDashboardGraphType,
    MetricsDashboardGraphTypeAggregation, MetricsDashboardGraphTypeDisplayType,
} from "../../../__generated__/api";
import Confirm from "../../../components/confirm/Confirm";
import Select from "../../../components/select/Select";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import COMPONENT_TO_TYPE, { COMPONENT_TO_SETTINGS } from "../graphs";
import Styles from "./EditWidget.module.scss";
import { ReactComponent as NumbersIcon } from "../../../assets/icons/numbers.svg";

const DATA_SOURCE_LABELS = {
    [MetricsDashboardGraphDataSource.Tickets]: "Tickets",
    [MetricsDashboardGraphDataSource.Sla]: "SLAs",
    [MetricsDashboardGraphDataSource.Feedback]: "Feedback",
    [MetricsDashboardGraphDataSource.Tags]: "Tags",
};

export const GRAPH_TYPES = {
    [MetricsDashboardGraphType.PieChart]: {
        name: "Pie Chart",
        icon: <RiPieChartLine className={Styles.icon} />,
    },
    [MetricsDashboardGraphType.TimeSeries]: {
        name: "Time Series",
        icon: <RiCalendarTodoLine className={Styles.icon} />,
    },
    [MetricsDashboardGraphType.Table]: {
        name: "Table",
        icon: <RiTableLine className={Styles.icon} />,
    },
    [MetricsDashboardGraphType.Count]: {
        name: "Numbers",
        icon: <NumbersIcon className={Styles.icon} />,
    },
    [MetricsDashboardGraphType.BarGraph]: {
        name: "Bar Graph",
        icon: <RiBarChart2Line className={Styles.icon} />,
    },
    [MetricsDashboardGraphType.LineGraph]: {
        name: "Line Graph",
        icon: <RiLineChartLine className={Styles.icon} />,
    },

};

const DEFAULT_GRAPH = {
    title: "Ticket Count",
    dataSource: MetricsDashboardGraphDataSource.Tickets,
    type: MetricsDashboardGraphType.Count,
    height: 1,
    width: 2,
    filters: [],
    metrics: [
        {
            fieldId: "urn:fq:form-field:id",
            aggregation: MetricsDashboardGraphTypeAggregation.Count,
            display: MetricsDashboardGraphTypeDisplayType.Number,
        },
    ],
    dimensions: [],
};
export default function EditWidget({
    shouldShow, onClose, data, value, onSave,
}: {
    shouldShow: boolean;
    onClose: () => void;
    onSave?: (value: MetricsDashboardGraph) => void;
    data: MetricsDashboardData[] | undefined;
    value?: MetricsDashboardGraph;
}) {
    const [graph, setGraph] = useState<Partial<MetricsDashboardGraph>>(DEFAULT_GRAPH);

    useEffect(() => {
        setGraph(value ?? DEFAULT_GRAPH);
    }, [value]);
    const Component = COMPONENT_TO_TYPE[graph.type ?? MetricsDashboardGraphType.PieChart];

    const rowGroups = useMemo(() => data?.toIdMap("dataSource", rowData => {
        const columnIndexMap = rowData?.columns.map((row, index) => ({ id: row.id, index })).toIdMap("id", "index") ?? {};
        const labelMap = rowData?.labels.toIdMap("id", "label") ?? {};
        return {
            rows: rowData,
            columnIndexMap,
            labelMap,
        };
    }) ?? {}, [data]);

    const graphType = graph.type ?? MetricsDashboardGraphType.PieChart;
    const dataSource = rowGroups[graph.dataSource ?? MetricsDashboardGraphDataSource.Tickets];

    const selectedType = GRAPH_TYPES[graphType];
    const Settings = COMPONENT_TO_SETTINGS[graphType];
    return (
        <Confirm
            shouldShow={shouldShow}
            closeText="Cancel"
            size="xl"
            confirmText={onSave ? "Save" : undefined}
            title={value ? "Edit Widget" : "Add Widget"}
            bodyClassName={Styles.container}
            className={Styles["modal-dialog"]}
            onHide={onClose}
            onConfirm={() => {
                onSave?.(graph as MetricsDashboardGraph);
            }}
        >
            <div className={Styles.form}>
                <FormGroup
                    type="text"
                    label="Widget Name"
                    required
                    size="small"
                    value={graph.title}
                    onChange={evt => setGraph({ ...graph, title: evt.target.value })}
                />
                <FormGroup
                    label="Widget Type"
                    required
                    size="small"
                    helpDisplay="icon"
                    helpText="What is the type of widget you want to create?"
                >
                    <Select
                        value={{
                            value: graph.type ?? MetricsDashboardGraphType.PieChart,
                            data: selectedType.name,
                            label: (
                                <div className={Styles["widget-type"]}>
                                    {selectedType.icon}
                                    {selectedType.name}
                                </div>
                            ),
                        }}
                        onChange={type => setGraph({ ...graph, type: type.value as MetricsDashboardGraphType })}
                        options={Object.entries(GRAPH_TYPES).map(([key, current]) => ({
                            value: key,
                            data: current.name,
                            label: (
                                <div className={Styles["widget-type"]}>
                                    {current.icon}
                                    {current.name}
                                </div>
                            ),
                        }))}
                    />
                </FormGroup>
                <FormGroup
                    label="Data Source"
                    required
                    size="small"
                    helpDisplay="icon"
                    helpText="What is the type of widget you want to create?"
                >
                    <Select
                        value={{
                            value: graph.dataSource as MetricsDashboardGraphDataSource,
                            label: DATA_SOURCE_LABELS[graph.dataSource as MetricsDashboardGraphDataSource],
                        }}
                        onChange={type => setGraph({ ...graph, dataSource: type.value as MetricsDashboardGraphDataSource })}
                        options={Object.entries(DATA_SOURCE_LABELS).map(([key, label]) => ({
                            label,
                            value: key,
                        }))}
                    />
                </FormGroup>

                <Settings isAdmin graph={graph as MetricsDashboardGraph} onChange={setGraph} />
            </div>
            <div className={classNames(Styles.preview, "editable-background")}>
                <div className={Styles["preview-header"]}>
                    <RiEyeLine />
                    {" "}
                    Widget Preview
                </div>

                <div className={classNames(Styles["report-widget"], Styles[`report-widget-${graph.type?.toLowerCase()}`])}>

                    {dataSource
                        ? (
                            <Component
                                graph={graph as MetricsDashboardGraph}
                                data={dataSource.rows}
                                columnIndexMap={dataSource.columnIndexMap}
                                labelMap={dataSource.labelMap}
                            />
                        )
                        : null}
                </div>

            </div>
        </Confirm>
    );
}

EditWidget.defaultProps = {
    value: undefined,
    onSave: undefined,
};
